import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Button
} from '@mui/material';
import {
  CompareArrows,
  ViewSidebar,
  ViewDay,
  Receipt,
  FileDownload
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = `${API_URL}/api/v1`;

/**
 * ComparisonView - Three-panel comparison system
 * 
 * Layout:
 * - Left Panel: Version selector & timeline
 * - Center Panel: Comparison view (side-by-side, unified diff, or variance)
 * - Right Panel: Discrepancy summary
 * 
 * View Modes:
 * 1. Side-by-Side: Split-screen comparison
 * 2. Unified Diff: Git-style changelog
 * 3. Timecard Variance: Pay discrepancy view
 */
const ComparisonView = ({ entityType, versions }) => {
  const [viewMode, setViewMode] = useState('sideBySide'); // 'sideBySide', 'unified', 'variance'
  const [fromVersion, setFromVersion] = useState(null);
  const [toVersion, setToVersion] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-select baseline and latest version
  useEffect(() => {
    if (versions && versions.length >= 2) {
      // Find baseline (first version)
      const baseline = versions.find(v => v.version_type === 'baseline') || versions[0];
      // Find latest version
      const latest = versions[versions.length - 1];
      
      setFromVersion(baseline);
      setToVersion(latest);
    }
  }, [versions]);

  // Fetch comparison data when versions selected
  useEffect(() => {
    const fetchComparison = async () => {
      if (!fromVersion || !toVersion) return;

      setLoading(true);
      setError(null);

      try {
        // Determine endpoint based on entity type
        const endpoint = entityType === 'rotation' 
          ? `/version-links/rotation-version/${fromVersion.id}/compare/${toVersion.id}`
          : `/version-links/timecard-version/${fromVersion.id}/compare/${toVersion.id}`;

        const response = await axios.get(`${API_BASE}${endpoint}`);
        setComparison(response.data);

        // If variance mode, fetch discrepancies
        if (viewMode === 'variance' && entityType === 'rotation') {
          const discrepancyEndpoint = `/audit/rotation/${fromVersion.rotation_number}/discrepancies`;
          const discResponse = await axios.get(`${API_BASE}${discrepancyEndpoint}`);
          setDiscrepancies(discResponse.data.discrepancies || []);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load comparison');
      } finally {
        setLoading(false);
      }
    };

    fetchComparison();
  }, [fromVersion, toVersion, viewMode, entityType]);

  // Calculate total owed from discrepancies
  const totalOwed = discrepancies
    .filter(d => d.severity === 'critical')
    .reduce((sum, d) => sum + (d.amount_owed || 0), 0);

  return (
    <Box>
      {/* View mode toggle */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <CompareArrows sx={{ mr: 1, verticalAlign: 'middle' }} />
            Version Comparison
          </Typography>
          <Button
            startIcon={<FileDownload />}
            variant="outlined"
            size="small"
            onClick={() => alert('Export feature coming soon!')}
          >
            Export Report
          </Button>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          fullWidth
        >
          <ToggleButton value="sideBySide">
            <ViewSidebar sx={{ mr: 1 }} />
            Side-by-Side
          </ToggleButton>
          <ToggleButton value="unified">
            <ViewDay sx={{ mr: 1 }} />
            Unified Diff
          </ToggleButton>
          <ToggleButton value="variance">
            <Receipt sx={{ mr: 1 }} />
            Pay Variance
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Three-panel layout */}
      <Grid container spacing={2}>
        {/* Left Panel: Version Selector */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Versions to Compare
            </Typography>
            <Divider sx={{ my: 1 }} />
            
            {/* FROM version */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary">
                FROM Version
              </Typography>
              <Box sx={{ mt: 1 }}>
                {versions?.map(v => (
                  <Chip
                    key={v.id}
                    label={`v${v.version_number} - ${v.version_type}`}
                    onClick={() => setFromVersion(v)}
                    color={fromVersion?.id === v.id ? 'primary' : 'default'}
                    sx={{ mb: 0.5, width: '100%' }}
                  />
                ))}
              </Box>
            </Box>

            {/* TO version */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                TO Version
              </Typography>
              <Box sx={{ mt: 1 }}>
                {versions?.map(v => (
                  <Chip
                    key={v.id}
                    label={`v${v.version_number} - ${v.version_type}`}
                    onClick={() => setToVersion(v)}
                    color={toVersion?.id === v.id ? 'secondary' : 'default'}
                    sx={{ mb: 0.5, width: '100%' }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Center Panel: Comparison View */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '70vh', overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : !fromVersion || !toVersion ? (
              <Alert severity="info">
                Select two versions from the left panel to compare
              </Alert>
            ) : (
              <Box>
                {viewMode === 'sideBySide' && (
                  <SideBySideView 
                    fromVersion={fromVersion}
                    toVersion={toVersion}
                    comparison={comparison}
                  />
                )}
                {viewMode === 'unified' && (
                  <UnifiedDiffView 
                    fromVersion={fromVersion}
                    toVersion={toVersion}
                    comparison={comparison}
                  />
                )}
                {viewMode === 'variance' && (
                  <VarianceView 
                    fromVersion={fromVersion}
                    toVersion={toVersion}
                    comparison={comparison}
                    discrepancies={discrepancies}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Panel: Discrepancy Summary */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              Discrepancy Summary
            </Typography>
            <Divider sx={{ my: 1 }} />

            {viewMode === 'variance' && discrepancies.length > 0 ? (
              <Box>
                {/* Total owed */}
                {totalOwed > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      Total Owed: ${totalOwed.toFixed(2)}
                    </Typography>
                  </Alert>
                )}

                {/* Issues by severity */}
                {discrepancies.map((disc, idx) => (
                  <Alert 
                    key={idx}
                    severity={
                      disc.severity === 'critical' ? 'error' :
                      disc.severity === 'warning' ? 'warning' : 'info'
                    }
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      {disc.category}
                    </Typography>
                    <Typography variant="body2">
                      {disc.description}
                    </Typography>
                    {disc.amount_owed > 0 && (
                      <Typography variant="caption" color="error.main">
                        Owed: ${disc.amount_owed.toFixed(2)}
                      </Typography>
                    )}
                  </Alert>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {viewMode === 'variance' 
                  ? 'No discrepancies detected'
                  : 'Switch to Pay Variance view to see discrepancies'}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// Side-by-Side View Component
const SideBySideView = ({ fromVersion, toVersion, comparison }) => {
  if (!comparison) return null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Side-by-Side Comparison
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              From: v{fromVersion.version_number}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fromVersion.version_type} • {new Date(fromVersion.uploaded_at).toLocaleDateString()}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {/* Display version data */}
            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(comparison.from_data, null, 2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" color="secondary" gutterBottom>
              To: v{toVersion.version_number}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {toVersion.version_type} • {new Date(toVersion.uploaded_at).toLocaleDateString()}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {/* Display version data */}
            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(comparison.to_data, null, 2)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// Unified Diff View Component
const UnifiedDiffView = ({ fromVersion, toVersion, comparison }) => {
  if (!comparison || !comparison.changes) return null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Change Log
      </Typography>
      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
        From v{fromVersion.version_number} → v{toVersion.version_number}
      </Typography>
      
      {comparison.changes.map((change, idx) => (
        <Alert 
          key={idx}
          severity={
            change.change_type === 'added' ? 'success' :
            change.change_type === 'removed' ? 'error' : 'info'
          }
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2">
            {change.change_type.toUpperCase()}: {change.affected_entity}
          </Typography>
          <Typography variant="body2">
            {change.impact_summary}
          </Typography>
          {change.credit_delta_minutes && (
            <Chip 
              label={`${change.credit_delta_minutes > 0 ? '+' : ''}${(change.credit_delta_minutes / 60).toFixed(1)}h`}
              size="small"
              color={change.credit_delta_minutes > 0 ? 'success' : 'error'}
              sx={{ mt: 0.5 }}
            />
          )}
        </Alert>
      ))}
    </Box>
  );
};

// Variance View Component
const VarianceView = ({ fromVersion, toVersion, comparison, discrepancies }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Pay Variance Analysis
      </Typography>
      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
        Comparing company timecard vs actual rotation
      </Typography>

      {discrepancies && discrepancies.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {discrepancies.map((disc, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Chip 
                    label={disc.severity.toUpperCase()}
                    color={
                      disc.severity === 'critical' ? 'error' :
                      disc.severity === 'warning' ? 'warning' : 'info'
                    }
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="subtitle2" gutterBottom>
                    {disc.category}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {disc.description}
                  </Typography>
                  {disc.pwa_section && (
                    <Typography variant="caption" color="primary">
                      PWA: {disc.pwa_section}
                    </Typography>
                  )}
                </Box>
                {disc.amount_owed > 0 && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">
                      You are owed
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      ${disc.amount_owed.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Alert severity="success" sx={{ mt: 2 }}>
          No pay discrepancies detected. Your pay matches company records.
        </Alert>
      )}
    </Box>
  );
};

export default ComparisonView;
